import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import type { LocationProvider } from '../types/provider';
import { resolveProviderLogoUrl } from '../utils/resolveProviderLogoUrl';
import SplashIconUploadField from '../components/SplashIconUploadField';
import SplashCoverUploadField from '../components/SplashCoverUploadField';
import HomeSplashScreenPreview, {
  buildSplashPreviewPartners,
  type SplashPreviewTextColors,
} from '../components/HomeSplashScreenPreview';

type ProviderEntryForm = {
  provider_id: number;
  provider_event_id: number | '';
  cover_image: string;
  button_color: string;
  badge_color: string;
  title: string;
  date_text: string;
  location_text: string;
};

type TextColorsForm = SplashPreviewTextColors;

type FormState = {
  enabled: boolean;
  season_intro: string;
  heading: string;
  subheading: string;
  events_section_title: string;
  continue_button_label: string;
  elsewhere_button_label: string;
  search_button_label: string;
  footer_text: string;
  dismiss_mode: 'session' | 'forever' | 'none';
  text_colors: TextColorsForm;
  icons: {
    calendar: string;
    location: string;
    lock: string;
    continue_arrow: string;
  };
  selectedProviderIds: number[];
  providerEntries: Record<number, ProviderEntryForm>;
};

const DEFAULT_TEXT_COLORS: TextColorsForm = {
  season_intro: '#2563EB',
  heading: '#0B1F4B',
  subheading: '#334155',
  events_section_title: '#0B1F4B',
  continue_button: '#2563EB',
  continue_button_border: '#2563EB',
  footer_text: '#64748B',
  card_title: '#0B1F4B',
  card_meta: '#475569',
};

const EMPTY_ENTRY = (providerId: number): ProviderEntryForm => ({
  provider_id: providerId,
  provider_event_id: '',
  cover_image: '',
  button_color: '',
  badge_color: '',
  title: '',
  date_text: '',
  location_text: '',
});

const DEFAULT_FORM: FormState = {
  enabled: false,
  season_intro: "C'est la saison des festivals !",
  heading: 'Vous avez perdu quelque chose ?',
  subheading: 'Retrouvez rapidement vos objets sur l\'événement concerné.',
  events_section_title: 'Sélectionnez votre événement',
  continue_button_label: 'Continuer sur le site',
  elsewhere_button_label: "J'ai perdu ou trouvé un objet ailleurs",
  search_button_label: 'Rechercher un objet',
  footer_text: 'Service officiel et sécurisé des objets trouvés.',
  dismiss_mode: 'session',
  text_colors: DEFAULT_TEXT_COLORS,
  icons: {
    calendar: '',
    location: '',
    lock: '',
    continue_arrow: '',
  },
  selectedProviderIds: [],
  providerEntries: {},
};

function normalizeHexInput(value: string, fallback: string): string {
  const trimmed = value.trim();
  return /^#[0-9A-Fa-f]{6}$/.test(trimmed) ? trimmed.toUpperCase() : fallback;
}

function buildFormFromConfig(config: any): FormState {
  const entries: Record<number, ProviderEntryForm> = {};
  const selectedProviderIds: number[] = [];
  for (const entry of config?.provider_entries ?? []) {
    const id = Number(entry.provider_id);
    if (!id) continue;
    selectedProviderIds.push(id);
    entries[id] = {
      provider_id: id,
      provider_event_id: entry.provider_event_id ? Number(entry.provider_event_id) : '',
      cover_image: entry.cover_image ?? '',
      button_color: entry.button_color ?? '',
      badge_color: entry.badge_color ?? '',
      title: entry.title ?? '',
      date_text: entry.date_text ?? '',
      location_text: entry.location_text ?? '',
    };
  }

  const textColors = { ...DEFAULT_TEXT_COLORS };
  if (config?.text_colors && typeof config.text_colors === 'object') {
    for (const key of Object.keys(textColors) as Array<keyof TextColorsForm>) {
      textColors[key] = normalizeHexInput(String(config.text_colors[key] ?? ''), textColors[key]);
    }
  }

  return {
    enabled: Boolean(config?.enabled),
    season_intro: config?.season_intro ?? DEFAULT_FORM.season_intro,
    heading: config?.heading ?? DEFAULT_FORM.heading,
    subheading: config?.subheading ?? DEFAULT_FORM.subheading,
    events_section_title: config?.events_section_title ?? DEFAULT_FORM.events_section_title,
    continue_button_label: config?.continue_button_label ?? DEFAULT_FORM.continue_button_label,
    elsewhere_button_label: config?.elsewhere_button_label ?? DEFAULT_FORM.elsewhere_button_label,
    search_button_label: config?.search_button_label ?? DEFAULT_FORM.search_button_label,
    footer_text: config?.footer_text ?? DEFAULT_FORM.footer_text,
    dismiss_mode: config?.dismiss_mode ?? 'session',
    text_colors: textColors,
    icons: {
      calendar: config?.icons?.calendar ?? '',
      location: config?.icons?.location ?? '',
      lock: config?.icons?.lock ?? '',
      continue_arrow: config?.icons?.continue_arrow ?? '',
    },
    selectedProviderIds,
    providerEntries: entries,
  };
}

const TEXT_COLOR_FIELDS: Array<{ key: keyof TextColorsForm; label: string }> = [
  { key: 'season_intro', label: 'Accroche saison' },
  { key: 'heading', label: 'Titre principal' },
  { key: 'subheading', label: 'Sous-titre' },
  { key: 'events_section_title', label: 'Titre zone événements' },
  { key: 'continue_button', label: 'Bouton continuer (texte)' },
  { key: 'continue_button_border', label: 'Bouton continuer (bordure)' },
  { key: 'footer_text', label: 'Pied de modal' },
  { key: 'card_title', label: 'Cartes partenaires (titre)' },
  { key: 'card_meta', label: 'Cartes partenaires (dates / lieu)' },
];

const HomeSplashScreenPage: React.FC = () => {
  const [form, setForm] = React.useState<FormState>(DEFAULT_FORM);
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['home-splash-screen'],
    queryFn: () => apiService.getHomeSplashScreenConfig(),
  });

  const { data: providersData } = useQuery({
    queryKey: ['providers-splash-screen'],
    queryFn: () => apiService.getProviders({ status: 'active', limit: 500 }),
  });

  const providers = React.useMemo(
    () => [...(providersData?.providers ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [providersData]
  );

  const providersById = React.useMemo(
    () => new Map(providers.map((provider) => [provider.id, provider])),
    [providers]
  );

  const previewPartners = React.useMemo(
    () => buildSplashPreviewPartners(form.selectedProviderIds, form.providerEntries, providersById),
    [form.selectedProviderIds, form.providerEntries, providersById]
  );

  React.useEffect(() => {
    if (!data?.config) return;
    setForm(buildFormFromConfig(data.config));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiService.updateHomeSplashScreenConfig({
        enabled: form.enabled,
        season_intro: form.season_intro,
        heading: form.heading,
        subheading: form.subheading,
        events_section_title: form.events_section_title,
        continue_button_label: form.continue_button_label,
        elsewhere_button_label: form.elsewhere_button_label,
        search_button_label: form.search_button_label,
        footer_text: form.footer_text,
        dismiss_mode: form.dismiss_mode,
        text_colors: form.text_colors,
        icons: {
          calendar: form.icons.calendar || null,
          location: form.icons.location || null,
          lock: form.icons.lock || null,
          continue_arrow: form.icons.continue_arrow || null,
        },
        provider_entries: form.selectedProviderIds.map((providerId) => {
          const entry = form.providerEntries[providerId] ?? EMPTY_ENTRY(providerId);
          return {
            provider_id: providerId,
            provider_event_id: entry.provider_event_id === '' ? null : entry.provider_event_id,
            cover_image: entry.cover_image || null,
            button_color: entry.button_color || null,
            badge_color: entry.badge_color || null,
            title: entry.title || null,
            date_text: entry.date_text || null,
            location_text: entry.location_text || null,
          };
        }),
      }),
    onSuccess: (res) => {
      setSaveMessage(res.message || 'Splash screen enregistré.');
      setTimeout(() => setSaveMessage(null), 2200);
      void refetch();
    },
  });

  const toggleProvider = (provider: LocationProvider, checked: boolean) => {
    setForm((prev) => {
      const nextIds = checked
        ? [...prev.selectedProviderIds, provider.id]
        : prev.selectedProviderIds.filter((id) => id !== provider.id);
      const nextEntries = { ...prev.providerEntries };
      if (checked && !nextEntries[provider.id]) {
        nextEntries[provider.id] = {
          ...EMPTY_ENTRY(provider.id),
          button_color: provider.private_page_background_color ?? '',
        };
      }
      return { ...prev, selectedProviderIds: nextIds, providerEntries: nextEntries };
    });
  };

  const moveProvider = (providerId: number, direction: -1 | 1) => {
    setForm((prev) => {
      const index = prev.selectedProviderIds.indexOf(providerId);
      if (index < 0) return prev;
      const target = index + direction;
      if (target < 0 || target >= prev.selectedProviderIds.length) return prev;
      const nextIds = [...prev.selectedProviderIds];
      const [removed] = nextIds.splice(index, 1);
      nextIds.splice(target, 0, removed);
      return { ...prev, selectedProviderIds: nextIds };
    });
  };

  const updateEntry = (providerId: number, patch: Partial<ProviderEntryForm>) => {
    setForm((prev) => ({
      ...prev,
      providerEntries: {
        ...prev.providerEntries,
        [providerId]: { ...(prev.providerEntries[providerId] ?? EMPTY_ENTRY(providerId)), ...patch },
      },
    }));
  };

  const updateEntryColor = (
    providerId: number,
    field: 'button_color' | 'badge_color',
    value: string,
    fallback: string
  ) => {
    updateEntry(providerId, { [field]: normalizeHexInput(value, fallback) });
  };

  const updateTextColor = (key: keyof TextColorsForm, value: string) => {
    setForm((prev) => ({
      ...prev,
      text_colors: {
        ...prev.text_colors,
        [key]: value,
      },
    }));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Splash screen accueil
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Modal d&apos;accueil pour mettre en avant des événements partenaires. L&apos;aperçu se met à jour en temps réel.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Impossible de charger la configuration.</Alert>}
      {saveMessage && <Alert severity="success" sx={{ mb: 2 }}>{saveMessage}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Aperçu
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 720 }}>
              <HomeSplashScreenPreview
                seasonIntro={form.season_intro}
                heading={form.heading}
                subheading={form.subheading}
                eventsSectionTitle={form.events_section_title}
                continueButtonLabel={form.continue_button_label}
                searchButtonLabel={form.search_button_label}
                footerText={form.footer_text}
                icons={form.icons}
                textColors={form.text_colors}
                partners={previewPartners}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
            }
            label="Activer le splash screen sur la page d'accueil"
          />
          <FormControl fullWidth sx={{ mt: 2, maxWidth: 320 }}>
            <InputLabel>Fermeture</InputLabel>
            <Select
              label="Fermeture"
              value={form.dismiss_mode}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  dismiss_mode: e.target.value as FormState['dismiss_mode'],
                }))
              }
            >
              <MenuItem value="session">Masquer pour la session</MenuItem>
              <MenuItem value="forever">Masquer jusqu&apos;à la prochaine modification</MenuItem>
              <MenuItem value="none">Toujours afficher à chaque visite</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Textes</Typography>
          <Grid container spacing={2}>
            {([
              ['season_intro', 'Accroche saison'],
              ['heading', 'Titre principal'],
              ['subheading', 'Sous-titre'],
              ['events_section_title', 'Titre zone événements'],
              ['continue_button_label', 'Bouton continuer (mobile)'],
              ['elsewhere_button_label', 'Bouton ailleurs (mobile)'],
              ['search_button_label', 'Bouton rechercher (cartes)'],
              ['footer_text', 'Pied de modal'],
            ] as const).map(([key, label]) => (
              <Grid item xs={12} md={6} key={key}>
                <TextField
                  fullWidth
                  label={label}
                  value={form[key]}
                  onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                  multiline={key === 'subheading'}
                  minRows={key === 'subheading' ? 2 : 1}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Couleurs des textes</Typography>
          <Grid container spacing={2}>
            {TEXT_COLOR_FIELDS.map(({ key, label }) => (
              <Grid item xs={12} md={6} lg={4} key={key}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    label={label}
                    value={form.text_colors[key]}
                    onChange={(e) => updateTextColor(key, e.target.value)}
                  />
                  <TextField
                    type="color"
                    value={normalizeHexInput(form.text_colors[key], DEFAULT_TEXT_COLORS[key])}
                    onChange={(e) => updateTextColor(key, e.target.value.toUpperCase())}
                    sx={{ width: 72, minWidth: 72 }}
                    inputProps={{ 'aria-label': label }}
                  />
                </Stack>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Icônes SVG</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Uploadez un fichier SVG par icône. Laissez vide pour conserver l&apos;icône par défaut du site.
          </Typography>
          <Grid container spacing={3}>
            {([
              ['calendar', 'Calendrier'],
              ['location', 'Lieu'],
              ['lock', 'Cadenas (pied)'],
              ['continue_arrow', 'Flèche continuer'],
            ] as const).map(([key, label]) => (
              <Grid item xs={12} md={6} key={key}>
                <SplashIconUploadField
                  label={label}
                  value={form.icons[key]}
                  onChange={(url) =>
                    setForm((prev) => ({
                      ...prev,
                      icons: { ...prev.icons, [key]: url },
                    }))
                  }
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Partenaires affichés</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cochez les partenaires actifs à afficher. L&apos;ordre de sélection définit l&apos;ordre des cartes.
          </Typography>
          <Stack spacing={1}>
            {providers.map((provider) => {
              const checked = form.selectedProviderIds.includes(provider.id);
              const orderIndex = form.selectedProviderIds.indexOf(provider.id);
              return (
                <Box key={provider.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={checked}
                      onChange={(e) => toggleProvider(provider, e.target.checked)}
                    />
                    <Box
                      component="img"
                      src={resolveProviderLogoUrl(provider.logo_url ?? provider.logo)}
                      alt=""
                      sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 1, bgcolor: '#f8fafc' }}
                    />
                    <Typography sx={{ flex: 1 }}>{provider.name}</Typography>
                    {checked && (
                      <Stack direction="row" spacing={1}>
                        <Button size="small" onClick={() => moveProvider(provider.id, -1)} disabled={orderIndex === 0}>
                          Monter
                        </Button>
                        <Button
                          size="small"
                          onClick={() => moveProvider(provider.id, 1)}
                          disabled={orderIndex === form.selectedProviderIds.length - 1}
                        >
                          Descendre
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                  {checked && (
                    <Box sx={{ pl: 6, pt: 1, pb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <SplashCoverUploadField
                            label="Image de couverture"
                            value={form.providerEntries[provider.id]?.cover_image ?? ''}
                            onChange={(url) => updateEntry(provider.id, { cover_image: url })}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Couleur bouton
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              fullWidth
                              value={form.providerEntries[provider.id]?.button_color ?? ''}
                              onChange={(e) => updateEntry(provider.id, { button_color: e.target.value })}
                              placeholder={provider.private_page_background_color ?? '#1E4B8F'}
                            />
                            <TextField
                              type="color"
                              value={normalizeHexInput(
                                form.providerEntries[provider.id]?.button_color ?? '',
                                provider.private_page_background_color ?? '#1E4B8F'
                              )}
                              onChange={(e) =>
                                updateEntryColor(
                                  provider.id,
                                  'button_color',
                                  e.target.value.toUpperCase(),
                                  provider.private_page_background_color ?? '#1E4B8F'
                                )
                              }
                              sx={{ width: 72, minWidth: 72 }}
                              inputProps={{ 'aria-label': 'Couleur bouton' }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Couleur badge année
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              fullWidth
                              value={form.providerEntries[provider.id]?.badge_color ?? ''}
                              onChange={(e) => updateEntry(provider.id, { badge_color: e.target.value })}
                              placeholder={
                                form.providerEntries[provider.id]?.button_color ||
                                provider.private_page_background_color ||
                                '#1E4B8F'
                              }
                            />
                            <TextField
                              type="color"
                              value={normalizeHexInput(
                                form.providerEntries[provider.id]?.badge_color ?? '',
                                form.providerEntries[provider.id]?.button_color ||
                                  provider.private_page_background_color ||
                                  '#1E4B8F'
                              )}
                              onChange={(e) =>
                                updateEntryColor(
                                  provider.id,
                                  'badge_color',
                                  e.target.value.toUpperCase(),
                                  form.providerEntries[provider.id]?.button_color ||
                                    provider.private_page_background_color ||
                                    '#1E4B8F'
                                )
                              }
                              sx={{ width: 72, minWidth: 72 }}
                              inputProps={{ 'aria-label': 'Couleur badge année' }}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Titre (optionnel)"
                            value={form.providerEntries[provider.id]?.title ?? ''}
                            onChange={(e) => updateEntry(provider.id, { title: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Dates (optionnel)"
                            value={form.providerEntries[provider.id]?.date_text ?? ''}
                            onChange={(e) => updateEntry(provider.id, { date_text: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            label="Lieu (optionnel)"
                            value={form.providerEntries[provider.id]?.location_text ?? ''}
                            onChange={(e) => updateEntry(provider.id, { location_text: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="ID événement (optionnel)"
                            value={form.providerEntries[provider.id]?.provider_event_id ?? ''}
                            onChange={(e) =>
                              updateEntry(provider.id, {
                                provider_event_id: e.target.value === '' ? '' : Number(e.target.value),
                              })
                            }
                            helperText="Vide = dernier événement actif"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  <Divider />
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending || isLoading}
      >
        Enregistrer
      </Button>
    </Box>
  );
};

export default HomeSplashScreenPage;
